import { useEffect, useMemo, useReducer, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Accordion, Button, Form, SearchInput } from '@qodo/design-system';
import { useQueryClient } from '@tanstack/react-query';
import { NoResults } from 'icons/NoResults';
import { ExternalLink } from 'lucide-react';
import { useSyncAzureRepositoriesMutation } from 'network/mutations/useSyncAzureRepositoriesMutation';
import { QUERY_KEYS } from 'network/queries/consts';
import { useAvailableAzureRepositoriesQuery } from 'network/queries/useAvailableAzureRepositoriesQuery';
import { ROUTES } from 'routes/consts';
import { Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { useDebounceValue } from 'usehooks-ts';
import { spawn } from 'utils/translations';
import { Loader } from '../../Loader';
import { useAzureInstallation } from '../azureInstallationContext';
import {
  initialSelectionState,
  selectionReducer,
} from '../connectRepositoriesReducer';
import {
  CONNECTION_METHODS,
  FORM_IDS,
  OAUTH_PARAMS,
  QODO_TROUBLESHOOTING_LINK,
  SELECTION_ACTIONS,
} from '../consts';
import { OrgRow } from '../parts';
import { ErrorStatusMessage } from '../parts/ErrorStatusMessage';
import {
  clearConsentState,
  decodeErrorDescription,
  extractAxiosErrorMessage,
  isAzureStateValid,
  parseRawErrorString,
  validateConsentState,
} from '../utils';

export const ConnectRepositories = () => {
  const spawnStep = spawn('dialogs.azure-installation.steps.connect-repos');
  const spawnConsent = spawn('dialogs.azure-installation.consent');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    goNext,
    setCurrentStepAsFurthestStepReached,
    setFurthestStepReached,
    setEarliestStepAvailable,
    setIsNextEnabled,
    setIsPending,
    currentStepIndex,
  } = useMultiStepDialog();

  const {
    installationId,
    consentGranted,
    setConsentGranted,
    consentError,
    setConsentError,
    setSyncedCount,
    connectionMethod,
    setConnectionMethod,
    azureTenantId,
    setAzureTenantId,
  } = useAzureInstallation();

  const [{ selectedIds, expandedOrgs }, dispatch] = useReducer(
    selectionReducer,
    initialSelectionState,
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebounceValue(searchQuery, 300);

  const [isValidating, setIsValidating] = useState(!consentGranted);

  const {
    data: availableRepos,
    isLoading: isLoadingRepos,
    isError: isReposError,
    error: reposError,
    refetch: refetchRepos,
  } = useAvailableAzureRepositoriesQuery({
    installationId,
    connectionMethod,
    azureTenantId,
    options: {
      enabled: consentGranted || connectionMethod === CONNECTION_METHODS.PAT,
    },
  });
  const syncMutation = useSyncAzureRepositoriesMutation();
  const availableReposLength = availableRepos?.organizations.length;

  const unlockCurrentStep = () => {
    setCurrentStepAsFurthestStepReached();
    setEarliestStepAvailable(currentStepIndex);
  };

  const resetConsentAndRepos = () => {
    clearConsentState();
    setConsentGranted(false);
    setConsentError(undefined);
    setAzureTenantId(undefined);
    queryClient.removeQueries({
      queryKey: [QUERY_KEYS.AZURE_AVAILABLE_REPOSITORIES],
    });
  };

  useEffect(() => {
    if (consentGranted || connectionMethod === CONNECTION_METHODS.PAT) {
      unlockCurrentStep();
      setIsValidating(false);

      return;
    }

    const errorParam =
      searchParams.get('error') || searchParams.get('error_description');
    const errorDescription = searchParams.get('error_description');

    if (errorParam) {
      unlockCurrentStep();
      clearConsentState();
      setConsentGranted(false);
      const parsedError = parseRawErrorString(errorParam) ?? errorParam;

      setConsentError(
        errorDescription
          ? decodeErrorDescription(errorDescription)
          : parsedError,
      );
      setIsValidating(false);

      // Clean up OAuth params from URL
      const cleaned = new URLSearchParams(searchParams);

      OAUTH_PARAMS.forEach((param) => cleaned.delete(param));

      setSearchParams(cleaned, { replace: true });

      return;
    }

    const isValidState = validateConsentState() && isAzureStateValid();

    if (!isValidState) {
      navigate(ROUTES.AZURE_INSTALLATION_STEP_2);

      return;
    }

    const tenantParam = searchParams.get('tenant');

    if (tenantParam) {
      setAzureTenantId(tenantParam);
    }

    unlockCurrentStep();
    clearConsentState();
    setConsentGranted(true);
    setConsentError(undefined);
    setIsValidating(false);
  }, []);

  useEffect(() => {
    if (availableReposLength) {
      const connectedOrgs = availableRepos.organizations
        .filter((org) => org.isConnected)
        .map((org) => org.name);

      dispatch({
        type: SELECTION_ACTIONS.SET_EXPANDED_ORGS,
        names: connectedOrgs,
      });

      const allRepoIds = availableRepos.organizations.flatMap((org) =>
        org.repositories.filter((r) => !r.isUsed).map((r) => r.id),
      );

      dispatch({ type: SELECTION_ACTIONS.SELECT_ALL_REPOS, ids: allRepoIds });
    }
  }, [availableRepos]);

  useEffect(() => {
    setIsNextEnabled(selectedIds.size > 0);
  }, [selectedIds]);

  const filteredOrganizations = useMemo(() => {
    if (!availableRepos?.organizations) return [];

    if (!debouncedSearchQuery.trim()) return availableRepos.organizations;

    const query = debouncedSearchQuery.toLowerCase();

    return availableRepos.organizations
      .map((org) => ({
        ...org,
        repositories: org.repositories.filter((repo) =>
          repo.name.toLowerCase().includes(query),
        ),
      }))
      .filter((org) => org.repositories.length > 0);
  }, [availableRepos, debouncedSearchQuery]);

  const methods = useForm();

  const onSyncRepositories = async () => {
    setIsPending(true);

    try {
      const result = await syncMutation.mutateAsync({
        repositoryIds: Array.from(selectedIds),
        azureTenantId,
      });

      setSyncedCount(result.syncedCount);

      goNext();
    } catch {
      setIsPending(false);
    }
  };

  const onRetryConsent = () => {
    resetConsentAndRepos();
    // In case of retry we want to default back to App Registration method as it's the recommended
    // one and doesn't require user to input anything
    setConnectionMethod(CONNECTION_METHODS.APP_REGISTRATION);
    setEarliestStepAvailable(0);
    setFurthestStepReached(0);
    navigate(ROUTES.AZURE_INSTALLATION_STEP_0, { replace: true });
  };

  const onOpenDocumentation = () => {
    window.open(QODO_TROUBLESHOOTING_LINK, '_blank');
  };

  if (isValidating) {
    return (
      <Step
        title={spawnStep('title')}
        description={spawnStep('description')}
        className="flex-1 h-full"
      >
        <div className="flex justify-center items-center flex-1">
          <Loader className="size-8" />
        </div>
      </Step>
    );
  }

  if (consentError) {
    return (
      <Step
        title={spawnStep('title')}
        description={spawnStep('description')}
        className="flex-1 h-full"
      >
        <ErrorStatusMessage
          message={spawnConsent('error')}
          retryLabel={spawnConsent('retry-button')}
          detail={consentError || spawnConsent('unknown-error')}
          onRefetch={onRetryConsent}
          urlLink={QODO_TROUBLESHOOTING_LINK}
        />
      </Step>
    );
  }

  const isReady = !isLoadingRepos && !isReposError && !!availableRepos;
  const orgLength = filteredOrganizations.length;

  const renderFilteredOrganizations = () => {
    return filteredOrganizations.map((org) => (
      <OrgRow
        key={org.name}
        org={org}
        orgsLength={orgLength}
        selectedIds={selectedIds}
        onToggleOrganization={(ids) =>
          dispatch({
            type: SELECTION_ACTIONS.TOGGLE_ORG_REPOS,
            ids,
          })
        }
        onToggleProjectRepos={(ids) =>
          dispatch({
            type: SELECTION_ACTIONS.TOGGLE_PROJECT_REPOS,
            ids,
          })
        }
        onToggleRepository={(id) =>
          dispatch({ type: SELECTION_ACTIONS.TOGGLE_REPO, id })
        }
      />
    ));
  };

  const renderNothingWasFoundIfNeeded = () => {
    const hasAvailableRepos =
      !!availableReposLength && availableReposLength > 0;
    const hasFilteredResults = filteredOrganizations.length > 0;

    if (!hasAvailableRepos || hasFilteredResults) {
      return <></>;
    }

    return (
      <div className="flex items-center">
        <span className="text-sm text-neutral-500">
          {spawnStep('no-results')}
        </span>
      </div>
    );
  };

  const renderFormContent = () => {
    if (isReposError) {
      return (
        <ErrorStatusMessage
          message={spawnStep('fetch-error')}
          retryLabel={spawnStep('retry-button')}
          onRefetch={refetchRepos}
          urlLink={QODO_TROUBLESHOOTING_LINK}
          detail={
            extractAxiosErrorMessage(reposError) ??
            spawnConsent('unknown-error')
          }
        />
      );
    }

    if (!isReady) {
      return (
        <div className="flex items-center justify-center py-8 flex-1 h-full">
          <Loader className="size-8" />
        </div>
      );
    }

    if (availableReposLength === 0) {
      return (
        <div className="flex flex-col justify-center items-center gap-2 py-4 h-full">
          <NoResults />
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2 max-w-110.75">
              <h3 className="w-full text-center text-white">
                {spawnStep('no-orgs-title')}
              </h3>
              <p className="w-full text-center text-text-general-neutral-mid">
                {spawnStep('no-orgs-description')}
              </p>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              variant="link"
              onClick={onOpenDocumentation}
              icon={ExternalLink}
              iconPosition="right"
            >
              {spawnConsent('need-help')}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-6 flex-1 min-h-0">
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">
            {selectedIds.size} {spawnStep('selected')}
          </span>
          <div className="min-w-82.5 relative">
            <SearchInput
              placeholder={spawnStep('search-placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2 flex-1 min-h-0 overflow-y-auto -mr-10 pr-10">
          <Accordion
            type="multiple"
            variant="filled"
            value={Array.from(expandedOrgs)}
            onValueChange={(names) =>
              dispatch({
                type: SELECTION_ACTIONS.SET_EXPANDED_ORGS,
                names,
              })
            }
          >
            {renderFilteredOrganizations()}
            {renderNothingWasFoundIfNeeded()}
          </Accordion>
        </div>
      </div>
    );
  };

  return (
    <Step
      title={spawnStep('title')}
      description={spawnStep('description')}
      className="flex-1 min-h-0"
    >
      <Form
        {...({ id: FORM_IDS.CONNECT_REPOS } as Record<string, string>)}
        methods={methods}
        onSubmit={onSyncRepositories}
        className="flex flex-col flex-1 min-h-0"
      >
        {renderFormContent()}
      </Form>
    </Step>
  );
};
